import { useNavigate } from "react-router-dom";
import { FiFile, FiEdit2, FiTrash2, FiChevronRight, FiCalendar, FiClock } from "react-icons/fi";
import { getWarrantyStatus } from "../SubscriptionsUtils/warrantyStatus";
import { exportToExcel } from "../SubscriptionsUtils/exportUtils";

export default function SubscriptionTable({ data, allData, onEdit, onDelete, onViewHistory }) {
  const navigate = useNavigate();

  const columns = [
    { header: "Name", key: "name" },
    { header: "Vendor", key: "vendor" },
    { header: "Billing Cycle", key: "billingCycle" },
    { 
      header: "Cost", 
      accessor: (row) => {
        const currency = row.costCurrency || "₹";
        const cost = row.cost || 0;
        return `${currency}${cost}`;
      }
    },
    { header: "Next Renewal", key: "dueDate" },
    {
      header: "Status",
      accessor: (row) => {
        const expiry = getWarrantyStatus(row.dueDate);
        return `${expiry.label}${expiry.daysLeft !== undefined ? ` (${expiry.daysLeft}d)` : ""}`;
      },
    },
  ];

  const handleExportExcel = () => {
    const exportData = allData || data;
    exportToExcel(exportData, columns, "subscriptions", "Subscriptions");
  };

  // Format date nicely
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getStatusBadge = (expiry) => {
    const styles = {
      red: "bg-red-50 text-red-700 border border-red-200",
      orange: "bg-orange-50 text-orange-700 border border-orange-200",
      yellow: "bg-amber-50 text-amber-700 border border-amber-200",
      green: "bg-emerald-50 text-emerald-700 border border-emerald-200",
      gray: "bg-gray-50 text-gray-600 border border-gray-200"
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${styles[expiry.color] || styles.gray}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${
          expiry.color === "red" ? "bg-red-500" :
          expiry.color === "orange" ? "bg-orange-500" :
          expiry.color === "yellow" ? "bg-amber-500" :
          expiry.color === "green" ? "bg-emerald-500" :
          "bg-gray-400"
        }`}></span>
        {expiry.label}
        {expiry.daysLeft !== undefined && (
          <span className="opacity-75">({expiry.daysLeft}d)</span>
        )}
      </span>
    );
  };

  const getBillingBadge = (cycle) => {
    if (!cycle) return <span className="text-gray-400">—</span>;
    
    const colors = {
      "Monthly": "bg-blue-50 text-blue-700 border-blue-200",
      "Quarterly": "bg-purple-50 text-purple-700 border-purple-200",
      "Yearly": "bg-indigo-50 text-indigo-700 border-indigo-200",
      "Annual": "bg-indigo-50 text-indigo-700 border-indigo-200",
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${colors[cycle] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
        {cycle}
      </span>
    );
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      {/* Export Buttons */}
      {data.length > 0 && (
        <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <p className="text-sm text-gray-500">
            <span className="font-semibold text-gray-900">{data.length}</span> subscription{data.length !== 1 ? 's' : ''}
          </p>
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
          >
            <FiFile className="text-green-600" />
            Export Excel
          </button>
        </div>
      )}

      {/* Mobile Card View - visible on small screens */}
      <div className="block md:hidden divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
        {data.map((sub) => {
          const expiry = getWarrantyStatus(sub.dueDate);

          return (
            <div
              key={sub._id}
              onClick={() => navigate(`/subscriptions/${sub._id}`)}
              className="p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
            >
              {/* Header row with name and status */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate text-sm">
                    {sub.name}
                  </h3>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {sub.vendor}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {getStatusBadge(expiry)}
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-3">
                <div>
                  <span className="text-gray-500">Billing:</span>
                  <span className="ml-1 text-gray-900">{sub.billingCycle || "—"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Renewal:</span>
                  <span className="ml-1 text-gray-900">{formatDisplayDate(sub.dueDate)}</span>
                </div>
              </div>

              {/* Actions row */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(sub);
                    }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium py-1 px-2 rounded-md hover:bg-blue-50 transition-colors"
                  >
                    <FiEdit2 className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  {onViewHistory && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewHistory(sub);
                      }}
                      className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium py-1 px-2 rounded-md hover:bg-purple-50 transition-colors"
                    >
                      <FiClock className="w-3.5 h-3.5" />
                      History
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(sub._id);
                    }}
                    className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium py-1 px-2 rounded-md hover:bg-red-50 transition-colors"
                  >
                    <FiTrash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </div>
                <FiChevronRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          );
        })}

        {data.length === 0 && (
          <div className="p-8 text-center text-gray-500 text-sm">
            No subscriptions found
          </div>
        )}
      </div>

      {/* Desktop Table View - hidden on small screens */}
      <div className="hidden md:block max-h-[60vh] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200">
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Billing
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Next Renewal
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {data.map((sub, index) => {
              const expiry = getWarrantyStatus(sub.dueDate);

              return (
                <tr
                  key={sub._id}
                  onClick={() => navigate(`/subscriptions/${sub._id}`)}
                  className={`group cursor-pointer transition-all duration-150 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                  } hover:bg-blue-50/50`}
                >
                  {/* Subscription Name & Vendor */}
                  <td className="px-6 py-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate max-w-[250px] group-hover:text-blue-600 transition-colors">
                        {sub.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate max-w-[250px] mt-0.5">
                        {sub.vendor || "No vendor"}
                      </p>
                    </div>
                  </td>

                  {/* Billing Cycle */}
                  <td className="px-6 py-4">
                    {getBillingBadge(sub.billingCycle)}
                  </td>

                  {/* Next Renewal Date */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-gray-700">
                      <FiCalendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium">
                        {formatDisplayDate(sub.dueDate)}
                      </span>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4">
                    {getStatusBadge(expiry)}
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(sub);
                        }}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Edit"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      {onViewHistory && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewHistory(sub);
                          }}
                          className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                          title="View History"
                        >
                          <FiClock className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(sub._id);
                        }}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                      <FiChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all ml-1" />
                    </div>
                  </td>
                </tr>
              );
            })}

            {data.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <div className="text-gray-400">
                    <FiFile className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">No subscriptions found</p>
                    <p className="text-xs mt-1">Try adjusting your filters</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
